import { useState, type MouseEvent } from "react";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { styled } from "../../../../lib/styled";
import {
  AUTO_VALIDATE_THRESHOLD,
  SCORECARD,
  TIER_COLOR,
  type ExtractionTier,
  type ScorecardKey,
} from "@shared/scorecard";
import type { TierClearedStatus } from "../../../../lib/corpusApi";
import { fieldLabel } from "../../../../lib/scorecardFieldUtils";

interface Props {
  status: TierClearedStatus | null;
  /** Per-field extraction confidence — drives the info popover's per-field breakdown. Null (no
   *  popover trigger) for rows predating Tangrowth rollout, same as `status`. */
  confidenceScores: Record<ScorecardKey, number> | null;
}

const TIERS: ExtractionTier[] = [1, 2, 3];

/** Maps each tier to its key on TierClearedStatus, avoiding a template-string cast at each call site. */
const TIER_STATUS_KEY: Record<ExtractionTier, keyof TierClearedStatus> = {
  1: "tier1",
  2: "tier2",
  3: "tier3",
};

/** Every scorecard field, grouped by tier, in SCORECARD's declared order. */
const FIELDS_BY_TIER: Record<ExtractionTier, ScorecardKey[]> = (() => {
  const groups: Record<ExtractionTier, ScorecardKey[]> = { 1: [], 2: [], 3: [] };
  for (const key of Object.keys(SCORECARD) as ScorecardKey[]) {
    groups[SCORECARD[key].tier].push(key);
  }
  return groups;
})();

const BadgeRow = styled(Stack)({
  alignItems: "center",
});

const PopoverContent = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  minWidth: 260,
}));

const TierDivider = styled(Divider)(({ theme }) => ({
  margin: `${theme.spacing(0.5)} 0`,
}));

const TierHeading = styled(Typography)<{ $color: string }>(({ $color }) => ({
  color: $color,
  lineHeight: 1.2,
}));

const FieldRow = styled(Stack)({
  justifyContent: "space-between",
  alignItems: "center",
});

const ClearedLabel = styled(Typography)<{ $cleared: boolean }>(({ theme, $cleared }) => ({
  color: $cleared ? theme.palette.success.main : theme.palette.text.secondary,
}));

/**
 * The T1/T2/T3 cleared-status badges shown on document rows (Documents, Improve Ranking), plus an
 * info button opening a per-field breakdown: every scorecard field's confidence and whether it
 * cleared its tier's AUTO_VALIDATE_THRESHOLD. Display-only — mirrors what deriveTierClearedStatus
 * already computed, doesn't affect ingestion gating (Tier 1 only, via evaluateIngestDecision).
 */
export function TierClearedBadges({ status, confidenceScores }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!status) return <>—</>;

  function openInfo(e: MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget);
  }

  function closeInfo() {
    setAnchorEl(null);
  }

  return (
    <BadgeRow direction="row" gap={0.5}>
      {TIERS.map((tier) => {
        const cleared = status[TIER_STATUS_KEY[tier]] === "cleared";
        return (
          <Chip
            key={tier}
            label={`T${tier}`}
            size="small"
            color={cleared ? "success" : "default"}
            variant={cleared ? "filled" : "outlined"}
          />
        );
      })}

      {confidenceScores && (
        <>
          <IconButton size="small" onClick={openInfo} aria-label="Field-level confidence breakdown">
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={closeInfo}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <PopoverContent gap={1}>
              {TIERS.map((tier, i) => (
                <Stack key={tier} gap={0.5}>
                  {i > 0 && <TierDivider />}
                  <TierHeading variant="overline" $color={TIER_COLOR[tier]}>
                    Tier {tier} — clears at {Math.round(AUTO_VALIDATE_THRESHOLD[tier] * 100)}%
                  </TierHeading>
                  {FIELDS_BY_TIER[tier].map((field) => {
                    const confidence = confidenceScores[field] ?? null;
                    const cleared = (confidence ?? 0) >= AUTO_VALIDATE_THRESHOLD[tier];
                    return (
                      <FieldRow key={field} direction="row">
                        <Typography variant="body2">{fieldLabel(field)}</Typography>
                        <ClearedLabel variant="caption" $cleared={cleared}>
                          {confidence !== null ? `${Math.round(confidence * 100)}%` : "—"}
                        </ClearedLabel>
                      </FieldRow>
                    );
                  })}
                </Stack>
              ))}
            </PopoverContent>
          </Popover>
        </>
      )}
    </BadgeRow>
  );
}
